import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import LoginIcon from "@mui/icons-material/Login";
import KeyIcon from "@mui/icons-material/Key";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "@react-keycloak/web";

import { KeycloackButton } from "../components/KeycloackButtonProvider";
import LanguageSwitcher from "../components/LanguageSwitcher";
import LoginForm from "../forms/Login";
import ForgotPasswordForm from "../forms/ForgotPasswordForm";
import { useAuth } from "../auth";

const fallback = "/dashboard" as const;

export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth?.isAuthenticated) {
      throw redirect({ to: search.redirect || fallback });
    }
  },
  component: LoginComponent,
  staticData: {
    title: "routes.login",
  },
});

function LoginComponent() {
  const { t } = useTranslation();
  const theme = useTheme();
  const ssoAllowed = true;
  const [showNormalLogin, setShowNormalLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { isAuthenticated, loginWithKeycloak } = useAuth();
  
  let keycloakContext: any;
  try {
    keycloakContext = useKeycloak();
  } catch (e) {
    keycloakContext = null;
  }

  const keycloakToken = keycloakContext?.keycloak?.token;
  const isKeycloakAuthenticated = keycloakContext?.keycloak?.authenticated;

  useEffect(() => {
    if (isKeycloakAuthenticated && keycloakToken && !isAuthenticated) {
      loginWithKeycloak(keycloakToken).catch((e) => {
        console.error("SSO Token Login error:", e);
      });
    }
  }, [isKeycloakAuthenticated, keycloakToken, isAuthenticated, loginWithKeycloak]);

  const onLogged = async () => {
    await navigate({ to: search.redirect || fallback, replace: true });
  };

  useEffect(() => {
    if (isAuthenticated) {
      void navigate({ to: search.redirect || fallback, replace: true });
    }
  }, [isAuthenticated, navigate, search.redirect]);

  if (isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </Box>
      <Stack
        component="section"
        justifyContent="center"
        alignItems="center"
        spacing={3}
        sx={{ width: "100%", maxWidth: 480, px: 2 }}
      >
        <Box
          sx={{
            textAlign: "center",
            mb: 2,
          }}
        >
          <img width="180px" src="/assets/logo.svg" alt="logo sosdocs" />
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: `0 8px 40px ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              {showForgotPassword ? (
                <LockResetIcon sx={{ fontSize: 28, color: "primary.main" }} />
              ) : (
                <LoginIcon sx={{ fontSize: 28, color: "primary.main" }} />
              )}
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              {showForgotPassword
                ? t("forgotPassword.title")
                : t("login.welcomeBack")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {showForgotPassword
                ? t("forgotPassword.subtitle")
                : t("login.accessYourAccount")}
            </Typography>
          </Box>

          <Stack spacing={2}>
            {showForgotPassword ? (
              <ForgotPasswordForm
                onBackToLogin={() => setShowForgotPassword(false)}
              />
            ) : (
              <>
                {ssoAllowed && (
                  <>
                    <KeycloackButton />
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t("common.or")}
                      </Typography>
                    </Divider>
                  </>
                )}

                {showNormalLogin ? (
                  <LoginForm
                    onLogged={onLogged}
                    onForgotPassword={() => setShowForgotPassword(true)}
                  />
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<KeyIcon />}
                    onClick={() => setShowNormalLogin(true)}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "1rem",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                      "&:hover": {
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                      },
                    }}
                  >
                    {t("login.loginWithSmartdocs")}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Paper>

        <Button
          variant="text"
          color="primary"
          onClick={() =>
            window.open(
              "/api/smartbpm/public/v1/redirect-manager/signer",
              "_blank",
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {t("login.accessDigitalSignature")}
        </Button>

        {window.versionSystem?.trim() && (
          <Typography variant="caption" color="text.secondary">
            {t("common.version")} {window.versionSystem}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
